<script lang="ts" setup>
import { Badge } from '@forgd/ui-kit/badge'
import { Button } from '@forgd/ui-kit/button'
import { Card } from '@forgd/ui-kit/card'
import { ArrowRightIcon } from '@heroicons/vue/24/outline'
import { useQuery } from '@pinia/colada'

interface ExploreTool {
  key: string
  label: string
  description: string
  icon: string
  to: string
  badge?: string
  videoSrc?: string
}

const auth = useAuth()

const tools = computed<ExploreTool[]>(() => [
  {
    key: 'token-designer',
    label: 'Design Tokenomics',
    description: 'Model emissions, vesting and demand. Publish a one-pager.',
    icon: '/icons/design-tokenomics.svg',
    to: '/design-tokenomics',
    badge: 'Pre-TGE',
    videoSrc: 'https://cdn.forgd.com/token-designer.mp4',
  },
  {
    key: 'engage-a-market-maker',
    label: 'Engage Market Makers',
    description: 'Run a competitive RFQ across top market makers.',
    icon: '/icons/engage-a-market-maker.svg',
    to: '/engage-a-market-maker/request-for-quotations',
    badge: 'Pre-TGE',
    videoSrc: 'https://cdn.forgd.com/engage-a-market-maker.mp4',
  },
  {
    key: 'exchange-listing',
    label: 'List on Exchanges',
    description: 'Research exchanges, listing performance and fees.',
    icon: '/icons/exchange-listing-research.svg',
    to: '/list-on-exchanges',
    badge: 'Pre-TGE',
    videoSrc: 'https://cdn.forgd.com/exchange-listing-research.mp4',
  },
  {
    key: 'market-maker-monitoring',
    label: 'Monitor Market Makers',
    description: 'Track depth, spreads, volume and uptime.',
    icon: '/icons/market-maker-monitoring-next.svg',
    to: '/market-maker-monitoring',
    badge: 'Post-TGE',
    videoSrc: 'https://cdn.forgd.com/market-maker-monitoring.mp4',
  },
  {
    key: 'token-unlocks',
    label: 'Track Token Unlocks',
    description: 'Visualise upcoming unlocks and emissions.',
    icon: '/icons/token-unlock-monitoring.svg',
    to: '/token-unlocks',
    badge: 'Post-TGE',
    videoSrc: 'https://cdn.forgd.com/token-unlocks.mp4',
  },
  {
    key: 'business-intelligence',
    label: 'Analyze Trade Activity',
    description: 'See historical price, volume and liquidity.',
    icon: '/icons/financial-analysis.svg',
    to: '/analyze-trade-activity',
    badge: 'Post-TGE',
    videoSrc: 'https://cdn.forgd.com/business-intelligence.mp4',
  },
])

const isPreTge = computed(() => auth.project?.tokenListed === false)
const projectId = computed(() => auth.project?.id)

const { data: stats } = useQuery({
  ...buildApiQuery('/projects/{projectId}/dashboard-stats', {
    params: () => ({ path: { projectId: projectId.value! } }),
  }),
  enabled: () => Boolean(auth.isLoggedIn && projectId.value),
  refetchOnWindowFocus: false,
})

const hasNoRfq = computed(() => {
  const status = stats.value?.mmRfqStatus
  return status === 'new' || status === 'undecided'
})

function dynamicBadge(tool: ExploreTool): string | undefined {
  if (!auth.isLoggedIn) {
    return undefined
  }
  if (tool.key === 'engage-a-market-maker') {
    return hasNoRfq.value ? 'Ready for Activation' : undefined
  }
  if (tool.badge === 'Post-TGE' && isPreTge.value) {
    return 'Research Only'
  }
  return undefined
}

const ROTATE_MS = 4000
const OPEN_HEIGHT = 134
const CLOSED_MIN_HEIGHT = 56

const activeIndex = ref(0)
const paused = ref(false)

watch(() => tools.value.length, (length) => {
  if (activeIndex.value >= length) {
    activeIndex.value = 0
  }
})

function advance() {
  activeIndex.value = (activeIndex.value + 1) % tools.value.length
}

function selectTool(index: number) {
  activeIndex.value = index
}
</script>

<template>
  <Card
    class="flex flex-col overflow-hidden"
    @mouseenter="paused = true"
    @mouseleave="paused = false"
    @focusin="paused = true"
    @focusout="paused = false"
  >
    <div class="flex h-14 shrink-0 items-center border-b border-default pr-3 pl-4">
      <h3 class="text-xs leading-4.5 font-semibold text-primal">
        Explore Free Tools
      </h3>
    </div>

    <ul class="flex min-h-0 flex-1 flex-col">
      <li
        v-for="(tool, i) in tools"
        :key="tool.key"
        class="overflow-hidden transition-[flex-basis,flex-grow] duration-200 ease-out"
        :class="i > 0 ? 'border-t border-default' : ''"
        :style="i === activeIndex
          ? { flex: `0 0 ${OPEN_HEIGHT}px` }
          : { flex: `1 1 ${CLOSED_MIN_HEIGHT}px` }"
      >
        <div v-if="i === activeIndex" class="relative h-full">
          <div
            class="explore-progress absolute inset-x-0 top-0 z-10 h-0.5 origin-left bg-[#2d73ff]"
            :class="{ 'explore-progress--paused': paused }"
            :style="{ animationDuration: `${ROTATE_MS}ms` }"
            @animationend="advance"
          />
          <div class="flex h-full items-stretch">
            <div class="flex min-w-0 flex-1 flex-col justify-between p-4">
              <div class="flex flex-col gap-1.5">
                <span class="text-xs leading-none font-semibold text-primal">{{ tool.label }}</span>
                <p class="text-xs leading-4.5 text-secondary">
                  {{ tool.description }}
                </p>
              </div>
              <div class="flex items-center gap-2">
                <Badge v-if="!dynamicBadge(tool) && tool.badge" variant="gray" class="border-0 whitespace-nowrap">
                  {{ tool.badge }}
                </Badge>
                <Button as-child variant="outline" size="sm" icon-only class="ml-auto">
                  <NuxtLink :to="tool.to" :aria-label="`Open ${tool.label}`">
                    <ArrowRightIcon />
                  </NuxtLink>
                </Button>
              </div>
            </div>
            <div
              class="w-46 shrink-0 bg-cover bg-center p-2.5 pb-0"
              style="background-image: url('/images/card-background.png')"
            >
              <video
                v-if="tool.videoSrc"
                :src="`${tool.videoSrc}#t=0.001`"
                autoplay
                muted
                loop
                playsinline
                class="size-full rounded-sm object-cover shadow-sm"
              />
              <img v-else :src="tool.icon" :alt="tool.label" class="size-full object-contain p-4">
            </div>
          </div>
        </div>

        <button
          v-else
          type="button"
          class="flex size-full items-center gap-2 px-4 text-left transition-colors hover:bg-secondary-subtle"
          @click="selectTool(i)"
        >
          <span class="text-xs leading-none font-semibold text-primal">{{ tool.label }}</span>
          <Badge v-if="dynamicBadge(tool)" variant="gray" class="border-0 whitespace-nowrap">
            {{ dynamicBadge(tool) }}
          </Badge>
          <img :src="tool.icon" :alt="tool.label" class="ml-auto size-8 shrink-0">
        </button>
      </li>
    </ul>
  </Card>
</template>

<style scoped>
.explore-progress {
  transform: scaleX(0);
  animation-name: explore-progress;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}

.explore-progress--paused {
  animation-play-state: paused;
}

@keyframes explore-progress {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}
</style>
